import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, Play, Square, X } from 'lucide-react';
import type { SatDomain, SatDomainMapEntryDto } from '@/api/sat';
import { Button } from '@/components/ui/button';
import {
  CHIPS,
  FLAG,
  STORY_FRAMES,
  STORY_UNDER_LINE,
  TINT_FILL_MS,
  TINT_STAGGER_MS,
  TRANSITION_MS,
  frame4SettledMs,
  ringStatesForFrame,
  type FrameNumber,
} from '@/components/marketing/storyFrames';
import { demoDomainMap } from '@/pages/open/openDemoDtos';
import { OpenFadeRegion } from '@/pages/open/OpenFadeRegion';
import { cn } from '@/lib/utils';
import { StopRing } from './StopRing';
import { COURSE_PREVIEW_COPY, SHORT_DOMAIN_LABELS, STUDENT_BAND_BAR_CLASSES } from './studentCopy';

/**
 * The first-run "course preview" — the shape of the course, shown as a promise
 * before the diagnostic exists (specs/009 FIRST-RUN).
 *
 * Eight empty rings, no trail. `See it fill in` plays THE film — the same
 * four-frame arc the homepage Story Stage plays (spec 010 amendment 2). Every
 * caption, ring value, band, sort order, flag and delay comes from
 * `components/marketing/storyFrames.ts`; nothing about the arc is defined here.
 *
 *   1  diagnostic  six answer chips arc in from the caption onto empty rings
 *   2  re-order    rings tint to their bands, then re-sort weakest-first
 *   3  practice    the weakest ring climbs, "You are here"
 *   4  prove it    it turns green and flags; the rest cascade to green flags
 *   -  drain       (course only) hold on the finished state, then everything
 *                  empties back to the real empty course; Start gets one pulse
 *
 * Pacing — a decision, not an accident: the FIRST play runs at exactly
 * `storyFrames` pacing (5s per frame, ~20.5s to the last flag, plus a short
 * rest on the finished state before it drains), because the owner asked for
 * the film to be slow enough to follow and the two doors must be the same film.
 * REPLAYS (`satOpenFillSeen`) run at `REPLAY_SPEED` of that (every duration and
 * delay multiplied, so the choreography keeps its shape): someone pressing the
 * button again has seen the story and wants the payoff sooner. Nothing else is
 * stored. Reduced motion steps through the same four frames as instant swaps.
 * It never blocks the page: chips ignore the pointer and `Stop` is available
 * the whole time.
 */

/** Rest on the finished (all green, all flagged) state before it drains. First-play ms. */
const FINAL_HOLD_MS = 2000;
/** Drain crossfade (course-only: the homepage stage just stops on frame 4). First-play ms. */
const DRAIN = { outMs: 300, inMs: 400, outroHoldMs: 2400 };
/** Reduced motion: each frame is an instant swap, this long apart. */
const REDUCED_STEP = 700;
const REDUCED_HOLD = 2100;
/** Replays run at this share of the first-play durations (~30% faster). */
export const REPLAY_SPEED = 0.7;
export const FILL_SEEN_KEY = 'satOpenFillSeen';

const STAGE_NAMES = ['idle', 'diagnostic', 'reorder', 'practice', 'prove'] as const;

/** 0 = at rest (the real empty course). */
type Frame = 0 | FrameNumber;

interface Chip {
  key: number;
  ok: boolean;
  x: number;
  y: number;
  dx: number;
  dy: number;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function readSeenAndMark(): boolean {
  try {
    const seen = window.localStorage.getItem(FILL_SEEN_KEY) != null;
    window.localStorage.setItem(FILL_SEEN_KEY, '1');
    return seen;
  } catch {
    return false;
  }
}

export function SatCoursePathPreview({
  domains,
  onFillEnd,
}: {
  domains: SatDomainMapEntryDto[];
  /** Fired when the drain completes — the hero uses it to nudge the Start button once. */
  onFillEnd?: () => void;
}) {
  const [frame, setFrame] = useState<Frame>(0);
  const [running, setRunning] = useState(false);
  const [outro, setOutro] = useState(false);
  const [instant, setInstant] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [drainKey, setDrainKey] = useState(0);
  // Built on press, never at render: the empty state is prerendered and must not read the clock.
  const [scores, setScores] = useState<Record<string, number> | null>(null);
  const [sortReady, setSortReady] = useState(false);
  const [chips, setChips] = useState<Chip[]>([]);
  const [active, setActive] = useState<SatDomain | null>(null);

  const rootRef = useRef<HTMLElement>(null);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const itemRefs = useRef<Partial<Record<SatDomain, HTMLLIElement | null>>>({});
  const restRects = useRef<Partial<Record<SatDomain, DOMRect>>>({});
  const timers = useRef<number[]>([]);
  const reducedRef = useRef(false);
  const onFillEndRef = useRef(onFillEnd);
  onFillEndRef.current = onFillEnd;

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  const courseDomains = useMemo(() => domains.map((d) => d.domain), [domains]);
  // Every ring value, band, order, flag and delay is storyFrames' — frame 0 reads as the empty frame 1.
  const states = useMemo(
    () => ringStatesForFrame(frame === 0 ? 1 : frame, courseDomains, frame === 0 ? null : scores),
    [frame, courseDomains, scores],
  );

  // Re-sorting moves stops with CSS `order`, not by reordering the DOM: a moved node
  // restarts its CSS animations, and the DOM/tab order stays the course's own.
  const sorted = frame >= 3 || (frame === 2 && (sortReady || reducedRef.current));
  const orderOf = (i: number) => (sorted ? states[i].order : i);

  const s = (ms: number) => Math.round(ms * speed);

  // FLIP: when the order flips to weakest-first, slide each stop from where it was.
  useLayoutEffect(() => {
    if (!sorted || frame !== 2 || reducedRef.current) return;
    for (const d of domains) {
      const el = itemRefs.current[d.domain];
      const from = restRects.current[d.domain];
      if (!el || !from) continue;
      const to = el.getBoundingClientRect();
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      if (!dx && !dy) continue;
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      void el.offsetWidth; // commit the inverted position before releasing it
      el.style.transition = `transform ${s(TRANSITION_MS)}ms cubic-bezier(0.3, 0.7, 0.2, 1)`;
      el.style.transform = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);

  /** Chips arc in from the caption onto the first six rings (same origin as the Story Stage). */
  const launchChips = () => {
    const root = rootRef.current?.getBoundingClientRect();
    const cap = captionRef.current?.getBoundingClientRect();
    if (!root || !cap) return;
    const sx = cap.left + Math.min(cap.width, 320) / 2 - root.left;
    const sy = cap.bottom - root.top;
    setChips(
      CHIPS.pattern.map((ok, i) => {
        const r = restRects.current[domains[i]?.domain];
        const tx = r ? r.left + r.width / 2 - root.left : sx;
        const ty = r ? r.top + 36 - root.top : sy;
        return { key: i, ok, x: sx, y: sy, dx: tx - sx, dy: ty - sy };
      }),
    );
  };

  const finish = () => {
    setInstant(true);
    setFrame(0);
    setSortReady(false);
    setChips([]);
    setRunning(false);
    setOutro(true);
    onFillEndRef.current?.();
  };

  const stop = () => {
    clearTimers();
    setInstant(true);
    setFrame(0);
    setSortReady(false);
    setChips([]);
    setRunning(false);
    setOutro(false);
  };

  const play = () => {
    if (running) return;
    clearTimers();
    const reduced = prefersReducedMotion();
    reducedRef.current = reduced;
    const k = reduced ? 1 : readSeenAndMark() ? REPLAY_SPEED : 1;
    const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms));
    const sk = (ms: number) => Math.round(ms * k);

    // Where every stop rests, before anything moves — chips aim at these and FLIP starts from them.
    restRects.current = {};
    for (const d of domains) {
      const r = itemRefs.current[d.domain]?.getBoundingClientRect();
      if (r) restRects.current[d.domain] = r;
    }

    // Same construction as the Story Stage: the diagnostic outcome of the demo student.
    const built: Record<string, number> = {};
    for (const d of demoDomainMap()) built[d.domain] = d.masterySnapshot.overall.scorePct ?? 0;

    setSpeed(k);
    setScores(built);
    setOutro(false);
    setInstant(reduced);
    setSortReady(false);
    setRunning(true);
    setFrame(1);

    if (reduced) {
      at(REDUCED_STEP, () => setFrame(2));
      at(REDUCED_STEP * 2, () => setFrame(3));
      at(REDUCED_STEP * 3, () => setFrame(4));
      at(REDUCED_STEP * 3 + REDUCED_HOLD, finish);
      return;
    }

    launchChips();
    at(sk(CHIPS.totalMs + 300), () => setChips([]));
    // Frame n starts when frames 1..n-1 have each held for their storyFrames holdMs.
    let start = 0;
    STORY_FRAMES.forEach((f) => {
      if (f.n > 1) at(sk(start), () => setFrame(f.n));
      if (f.n === 2) at(sk(start + TINT_STAGGER_MS * (courseDomains.length - 1) + TINT_FILL_MS + 300), () => setSortReady(true));
      start += f.holdMs;
    });
    const frame4Start = start - STORY_FRAMES[STORY_FRAMES.length - 1].holdMs;
    const drainAt = sk(frame4Start + frame4SettledMs(courseDomains.length) + FINAL_HOLD_MS);
    at(drainAt, () => setDrainKey((n) => n + 1));
    at(drainAt + sk(DRAIN.outMs), finish);
    at(drainAt + sk(DRAIN.outMs) + sk(DRAIN.outroHoldMs), () => setOutro(false));
  };

  const caption =
    frame > 0 ? STORY_FRAMES[frame - 1].caption : outro ? COURSE_PREVIEW_COPY.captions.outro : '';
  const activeEntry = domains.find((d) => d.domain === active);
  const anim = !instant && !reducedRef.current;
  const captionAnim = !reducedRef.current;

  return (
    <section
      ref={rootRef}
      className="relative space-y-4"
      data-testid="course-path-preview"
      data-fill-stage={STAGE_NAMES[frame]}
      data-fill-frame={frame}
    >
      <style>{`
        @keyframes sat-chip-x { from { transform: translateX(0); } to { transform: translateX(var(--dx)); } }
        @keyframes sat-chip-y { from { transform: translateY(0); } to { transform: translateY(var(--dy)); } }
        @keyframes sat-chip-life {
          0% { opacity: 0; scale: 0.5; } 25% { opacity: 1; scale: 1; }
          75% { opacity: 1; scale: 1; } 100% { opacity: 0; scale: 0.7; }
        }
        @keyframes sat-flag { from { opacity: 0; transform: scale(${FLAG.fromScale}); } to { opacity: 1; transform: scale(1); } }
        @keyframes sat-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sat-caption-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .sat-chip-x { animation-name: sat-chip-x; animation-timing-function: linear; animation-fill-mode: both; }
        .sat-chip-y { animation-name: sat-chip-y; animation-timing-function: cubic-bezier(0.55, 0, 0.95, 0.6); animation-fill-mode: both; }
        .sat-chip-life { animation-name: sat-chip-life; animation-timing-function: ease-out; animation-fill-mode: both; }
        .sat-flag { animation: sat-flag ${FLAG.popMs}ms ${FLAG.easing} both; }
        .sat-fade-in { animation: sat-fade-in 300ms ease-out both; }
        .sat-caption-in { animation: sat-caption-in 300ms ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .sat-flag, .sat-fade-in, .sat-caption-in { animation: none !important; }
        }
      `}</style>

      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h3 className="text-lg font-semibold tracking-tight">Your course: {domains.length} areas</h3>
          <p className="text-sm text-muted-foreground" data-testid="course-path-subtitle">
            {running || frame !== 0 ? STORY_UNDER_LINE : COURSE_PREVIEW_COPY.subtitle}
          </p>
          <p
            ref={captionRef}
            // Reserve the caption's height only while the film plays; at rest it would be a dead gap
            // above the button on a phone.
            className={cn('text-sm font-medium text-primary sm:min-h-5', (running || frame !== 0) && 'min-h-10')}
            aria-live="polite"
            data-testid="fill-caption"
          >
            {caption && (
              <span key={caption} className={captionAnim ? 'sat-caption-in inline-block' : 'inline-block'}>
                {caption}
              </span>
            )}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 gap-1.5"
          onClick={running ? stop : play}
          data-testid="fill-preview-button"
          data-running={running}
        >
          {running ? (
            <Square className="!size-3 fill-current" aria-hidden />
          ) : (
            <Play className="!size-3 fill-current" aria-hidden />
          )}
          {running ? COURSE_PREVIEW_COPY.stopButton : COURSE_PREVIEW_COPY.fillButton}
        </Button>
      </div>

      <div className="rounded-xl border bg-card px-4 pb-2 pt-8 sm:px-6">
        <OpenFadeRegion stateKey={`drain-${drainKey}`} outMs={s(DRAIN.outMs)} inMs={s(DRAIN.inMs)}>
          <ul className="grid grid-cols-4 gap-x-1 gap-y-5 sm:grid-cols-8">
            {domains.map((entry, origIdx) => {
              const st = states[origIdx];
              const fill = instant || frame === 0 ? 'instant' : { ms: s(st.fill === 'instant' ? 0 : st.fill.ms), delayMs: s(st.fill === 'instant' ? 0 : st.fill.delayMs) };
              const skills = entry.masterySnapshot.overall.totalSkillsInDomain;
              const hint = `${entry.domainLabel} · ${skills} skills · unlocks after the diagnostic`;
              const centerDelay = fill === 'instant' ? 0 : Math.round(fill.delayMs + fill.ms * 0.7);
              return (
                <li
                  key={entry.domain}
                  ref={(el) => {
                    itemRefs.current[entry.domain] = el;
                  }}
                  className="relative"
                  style={{ order: orderOf(origIdx) }}
                  data-order={orderOf(origIdx)}
                >
                  <button
                    type="button"
                    title={hint}
                    aria-label={hint}
                    data-testid={`preview-stop-${entry.domain}`}
                    data-mastered={st.mastered}
                    onMouseEnter={() => setActive(entry.domain)}
                    onMouseLeave={() => setActive((a) => (a === entry.domain ? null : a))}
                    onFocus={() => setActive(entry.domain)}
                    onBlur={() => setActive((a) => (a === entry.domain ? null : a))}
                    onClick={() => setActive(entry.domain)}
                    className="group flex w-full flex-col items-center gap-1.5 rounded-lg p-1 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span className="relative block">
                      {st.youAreHere && (
                        <span
                          key="here"
                          data-testid="fill-you-are-here"
                          aria-hidden
                          className={cn(
                            'pointer-events-none absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-semibold text-primary-foreground shadow',
                            anim && 'sat-fade-in',
                          )}
                        >
                          You are here
                        </span>
                      )}
                      <StopRing scorePct={st.pct} band={st.band} unearned fill={fill}>
                        <span className="z-10 text-[13px] font-bold tracking-tight text-foreground">
                          {st.pct != null ? (
                            <span
                              key={st.pct}
                              className={cn('block', anim && 'sat-fade-in')}
                              style={anim ? { animationDelay: `${centerDelay}ms` } : undefined}
                            >
                              {st.pct}%
                            </span>
                          ) : (
                            <span
                              className={cn('block h-2 w-2 rounded-full', STUDENT_BAND_BAR_CLASSES.NOT_STARTED)}
                              aria-hidden
                            />
                          )}
                        </span>
                      </StopRing>
                      {st.flagDelayMs != null && (
                        <span
                          key="flag"
                          data-testid="fill-flag"
                          aria-hidden
                          className={cn(
                            'absolute -right-1.5 -top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white shadow ring-2 ring-card',
                            anim && 'sat-flag',
                          )}
                          style={anim ? { animationDelay: `${s(st.flagDelayMs)}ms` } : undefined}
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={3.5} aria-hidden />
                        </span>
                      )}
                    </span>
                    <span className="text-balance text-xs font-medium leading-tight">
                      {SHORT_DOMAIN_LABELS[entry.domain]}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </OpenFadeRegion>
        <p className="mt-1 min-h-4 text-center text-xs text-muted-foreground" data-testid="preview-hint" aria-hidden>
          {activeEntry
            ? `${activeEntry.domainLabel} · ${activeEntry.masterySnapshot.overall.totalSkillsInDomain} skills · unlocks after the diagnostic`
            : ''}
        </p>
      </div>

      {chips.map((chip) => {
        const delay = `${s(Math.round(CHIPS.staggerMs * chip.key))}ms`;
        const flight = `${s(CHIPS.flightMs)}ms`;
        return (
          <span
            key={chip.key}
            aria-hidden
            data-testid="fill-chip"
            className="pointer-events-none absolute z-20"
            style={{
              left: chip.x - 12,
              top: chip.y - 12,
              ['--dx' as string]: `${chip.dx}px`,
              ['--dy' as string]: `${chip.dy}px`,
            }}
          >
            <span className="sat-chip-x block" style={{ animationDelay: delay, animationDuration: flight }}>
              <span className="sat-chip-y block" style={{ animationDelay: delay, animationDuration: flight }}>
                <span
                  className={cn(
                    'sat-chip-life flex h-6 w-6 items-center justify-center rounded-full text-white shadow-md',
                    chip.ok ? 'bg-emerald-500' : 'bg-rose-500',
                  )}
                  style={{ animationDelay: delay, animationDuration: flight }}
                >
                  {chip.ok ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <X className="h-3.5 w-3.5" strokeWidth={3} />}
                </span>
              </span>
            </span>
          </span>
        );
      })}
    </section>
  );
}
